import { css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { Commands, RouterLocation } from '@vaadin/router';
import { LitPage } from '../components/lit-page';

import puzzle from '../puzzles/wasmBridge';
import { Options, ViewModel } from '../puzzles/wasmBridge/viewModel';

import Puzzle, { getPuzzle } from '../puzzles/puzzleBase';
import { navigate } from '../navigation';
import { isPuzzleId, type PuzzleId } from '../components/luna-common';
import { getCurrentUser } from '../auth/session';
import { patch } from '../utils/http';
import { loadPresets, savePresets } from '../puzzles/preset-controls';
import { Preset, appendPreset } from '../puzzles/preset';
import { autoName } from '../puzzles/auto-name';
import { getFullCatalog, resetCatalog, type GameOptionDefinition } from '../puzzles/settings-catalog';
import { paletteForTheme } from '../puzzles/board-palettes';
import { getTheme } from '../theming';
import { resolveGameStart, shouldSaveOnLeave, type GameplayState } from '../puzzles/gameplay';
import { loadGameState, saveGameState, clearGameState } from '../puzzles/game-state';

import '../components/luna-button/luna-button';
import '../components/luna-icon/luna-icon';
import '../components/luna-skeleton/luna-skeleton';
import '../components/luna-confirm-dialog/luna-confirm-dialog';
import '../components/luna-toast/luna-toast';

@customElement('app-puzzle')
export class AppPuzzle extends LitPage {
  @property({ type: Boolean }) preview = false;
  @property({ type: Number }) level?: number = null;
  @property({ type: String }) puzzleName: string;

  @state() presets: { name: string; index: number }[] = [];
  @state() checkpointSaved = false;
  @state() atCheckpoint = false;
  @state() puzzleModel: Puzzle;
  @state() preset = '';
  @state() undoEnabled = false;
  @state() redoEnabled = false;
  @state() customOptions: GameOptionDefinition[] = [];
  @state() myPresets: Preset[] = [];
  @state() gameplayState: GameplayState = 'loading';
  @state() savedToPresets = false;
  @state() private _confirmAction: 'restart' | 'new-game' | null = null;
  @state() private _toast: { role: 'success' | 'info'; message: string } | null = null;

  viewModel: ViewModel;

  // Path captured at first render; status events are tagged with it so the
  // shell can reject late async emits fired after the user has navigated away.
  private pagePath = '';
  private presetsLoaded = false;
  private currentOptions: Options[] | null = null;
  private originatedFromOptions = false;
  private starting = false;
  // Precache-completion quirk: for slow/chunked solves (map) the precache's
  // jsCompleted can land asynchronously AFTER `starting` has flipped back.
  // A real solve always requires at least one executed move, so completions
  // with an empty engine move list are always spurious (BR-139).
  // The save string most recently handed to the backend (BR-128 dedupe baseline).
  private lastSavedState: string | null = null;

  constructor() {
    super();

    this.handleResize = this.handleResize.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
  }

  private get isParticipant() {
    return getCurrentUser()?.trusted_metadata?.role !== 'carer';
  }

  onBeforeLeave(location: RouterLocation) {
    if (location.pathname === '/auth/callback') {
      navigate('/');
    }
  }

  onBeforeEnter(
    location: { params: { puzzleName?: string; level?: string } },
    commands: Commands
  ) {
    const name = location.params.puzzleName ?? '';
    if (!isPuzzleId(name)) {
      commands.redirect('/');
      return;
    }
    this.puzzleName = name;

    const levelParam = location.params.level;
    if (levelParam !== undefined) {
      const level = Number(levelParam);
      if (!Number.isInteger(level) || level < 0) {
        commands.redirect(`/puzzles/${name}/boards`);
        return;
      }
      this.level = level;
    }
  }

  async firstUpdated() {
    this.pagePath = window.location.pathname;
    await this.loadGame();
  }

  private async loadGame(): Promise<void> {
    this.gameplayState = 'loading';
    this.presets = [];

    try {
      this.puzzleModel = await getPuzzle(this.puzzleName);

      const catalog = await getFullCatalog();
      if (catalog === null) {
        this.gameplayState = 'error';
        return;
      }
      this.customOptions = catalog[this.puzzleName] ?? [];

      const loadedPresets = await loadPresets(this.puzzleName);
      this.presetsLoaded = loadedPresets !== null;
      this.myPresets = loadedPresets ?? [];

      this.puzzleModel.applyTheme(paletteForTheme(getTheme()));

      this.viewModel = await puzzle.initialise({
        name: this.puzzleName,
        colourOverrides: this.puzzleModel.colourOverrides,
        onPresetAdded: this.addPreset.bind(this),
        onUndoRedoEnabled: this.undoRedoEnabled.bind(this),
        onInitialise: this.puzzleModel.onInitialise.bind(this.puzzleModel),
        onMove: this.onMove.bind(this),
        onCompleted: this.onCompleted.bind(this),
      });

      if (!this.viewModel) {
        this.gameplayState = 'error';
        return;
      }

      await this.startFromUrl();
    } catch {
      this.gameplayState = 'error';
    }
  }

  private async retryLoad(): Promise<void> {
    resetCatalog();
    this.gameplayState = 'loading';
    await this.updateComplete; // canvas must be mounted before wasm initialise
    await this.loadGame();
  }

  private async startFromUrl(): Promise<void> {
    this.preview = new URLSearchParams(window.location.search).get('preview') === 'true';

    const resolution = resolveGameStart(
      window.location.search,
      this.level,
      this.myPresets,
      this.presets.map((preset) => preset.index),
      this.customOptions,
      this.puzzleName as PuzzleId
    );

    switch (resolution.kind) {
      case 'resume':
        await this.resumeFromBackend();
        break;
      case 'level':
        this.setPreset(resolution.level);
        break;
      case 'saved':
        this.applyPreset(resolution.preset);
        break;
      case 'custom':
        this.startGameWithOptions(
          resolution.label ?? autoName(this.puzzleName, resolution.options, this.customOptions),
          resolution.options,
          true
        );
        break;
      case 'redirect':
        navigate(resolution.path);
        break;
    }
  }

  private async resumeFromBackend(): Promise<void> {
    const row = await loadGameState(this.puzzleName as PuzzleId);
    if (!row || !row.state) {
      navigate(`/puzzles/${this.puzzleName}/boards`);
      return;
    }

    this.level = row.level ?? null;

    if (row.level !== null) {
      this.currentOptions = null;
      this.originatedFromOptions = false;
      this.preset = this.presets.find((preset) => preset.index === row.level)?.name ?? '';
    } else if (row.options) {
      this.currentOptions = row.options;
      this.originatedFromOptions = true;
      this.preset = row.label;
    } else {
      this.currentOptions = null;
      this.originatedFromOptions = false;
      this.preset = row.label;
    }

    this.checkpointSaved = row.checkpoint !== null;
    this.atCheckpoint = false;

    this.starting = true;
    this.viewModel.resumeGame(row.state);
    if (row.checkpoint) {
      this.viewModel.restoreCheckpointFrom(row.checkpoint);
    }
    this.starting = false;

    this.lastSavedState = row.state;
    this.gameplayState = 'playing';
    this.emitStatus(this.preset);

    requestAnimationFrame(() => this.handleResize());
  }

  private emitStatus(label: string): void {
    window.dispatchEvent(
      new CustomEvent('luna-status-chip', {
        detail: { label, path: this.pagePath },
      })
    );
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('mbw:navigate-away', this.onLeave);
    window.addEventListener('pagehide', this.onLeave);
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('mbw:navigate-away', this.onLeave);
    window.removeEventListener('pagehide', this.onLeave);
    document.removeEventListener('visibilitychange', this.onVisibility);
    super.disconnectedCallback();
  }

  private handleResize() {
    if (!this.viewModel) {
      return;
    }

    const puzzleCanvas = this.renderRoot.querySelector('#puzzle-canvas');
    if (!puzzleCanvas) {
      return;
    }

    const rect = puzzleCanvas.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      return;
    }

    this.viewModel.resizePuzzle(puzzleCanvas.clientWidth, puzzleCanvas.clientHeight);
  }

  private undoRedoEnabled(undo: boolean, redo: boolean) {
    this.undoEnabled = undo;
    this.redoEnabled = redo;
    this.atCheckpoint = this.viewModel?.isAtCheckpoint() ?? false;
  }

  // BR-139: the per-game js_move hook is only implemented by Unruly, so the
  // engine's serialised move list is the only cross-game "has moved" signal.
  // It grows on moves, shrinks on undo, and is empty after a restart.
  private hasMoves(): boolean {
    return (this.viewModel?.state.length ?? 0) > 0;
  }

  private onMove(move: string) {
    this.puzzleModel.onMove(move);
    this.atCheckpoint = this.viewModel?.isAtCheckpoint() ?? false;
  }

  private onLeave = () => {
    this.saveOnLeave();
  };

  private onVisibility = () => {
    if (document.visibilityState === 'hidden') {
      this.saveOnLeave();
    }
  };

  private saveOnLeave(): void {
    if (!this.viewModel) {
      return;
    }

    const state = this.viewModel.getSaveFileString();
    const shouldSave = shouldSaveOnLeave(
      this.gameplayState,
      this.hasMoves(),
      state,
      this.lastSavedState
    );
    if (!shouldSave) {
      return;
    }

    this.lastSavedState = state;
    saveGameState(this.puzzleName as PuzzleId, {
      level: this.level ?? null,
      options: this.currentOptions ?? null,
      label: this.preset,
      state,
      checkpoint: this.viewModel.checkpoint ?? null,
    });
  }

  private async onCompleted() {
    if (this.starting || !this.hasMoves()) {
      return; // ignore the solve-triggered completion emitted while precaching the solution
    }

    this.gameplayState = 'solved';
    this.emitStatus('Solved!');

    if (this.level !== null) {
      const response = await patch(`participant/${getCurrentUser().emails[0].email}/programme`, {
        puzzle: this.puzzleName,
        level: this.level,
      });

      if (response.error) {
        console.error('Error reporting puzzle completion:', response.error);
      }
    } else if (this.originatedFromOptions && this.currentOptions) {
      const response = await patch(`participant/${getCurrentUser().emails[0].email}/programme`, {
        puzzle: this.puzzleName,
        options: this.currentOptions,
        label: this.preset,
      });

      if (response.error) {
        console.error('Error reporting puzzle completion:', response.error);
      }
    }

    const saved = await this.saveOnCompletion();
    this.savedToPresets = saved;

    clearGameState(this.puzzleName as PuzzleId);
  }

  private async saveOnCompletion(): Promise<boolean> {
    if (!this.originatedFromOptions || !this.currentOptions) {
      return false;
    }

    if (!this.presetsLoaded) {
      return false;
    }

    const name = autoName(this.puzzleName, this.currentOptions, this.customOptions);
    if (!name) {
      return false;
    }

    const next = appendPreset(this.myPresets, { options: this.currentOptions });
    if (next === null) {
      return false; // identical config already saved — no-op
    }

    const result = await savePresets(this.puzzleName, next);
    if (result.presets === null) {
      return false;
    }

    this.myPresets = result.presets;
    return true;
  }

  private addPreset(name: string, index: number) {
    name = name.toLowerCase().replace('x', ' x ').replace('...', '');

    if (index === -1) {
      return;
    }

    const preset = { name, index };
    this.presets = [...this.presets, preset];
  }

  private setPreset(index: number) {
    this.currentOptions = null;
    this.originatedFromOptions = false;
    this.starting = true;
    this.checkpointSaved = false;
    this.atCheckpoint = false;
    this.viewModel.boardSizePreset = index;
    this.starting = false;
    const found = this.presets.find((preset) => preset.index === index);
    this.preset = found ? found.name : '';
    this.gameplayState = 'playing';
    this.emitStatus(this.preset);

    requestAnimationFrame(() => this.handleResize());
  }

  private applyPreset(preset: Preset) {
    this.startGameWithOptions(autoName(this.puzzleName, preset.options, this.customOptions), preset.options, true);
  }

  private startGameWithOptions(name: string, options: Options[], saveable: boolean) {
    this.currentOptions = options;
    this.originatedFromOptions = saveable;
    this.starting = true;
    this.checkpointSaved = false;
    this.atCheckpoint = false;
    this.viewModel.options = options;
    this.starting = false;
    this.preset = name;
    this.gameplayState = 'playing';
    this.emitStatus(name);

    requestAnimationFrame(() => this.handleResize());
  }

  private saveCheckpoint() {
    this.viewModel.saveCheckpoint();
    this.checkpointSaved = true;
    this.atCheckpoint = true;
  }

  private restoreCheckpoint() {
    this.viewModel.restoreCheckpoint();
    this.atCheckpoint = true;
  }

  private hint() {
    this.puzzleModel.hint(this.viewModel, this.preset);
  }

  private onKeyDown(event: KeyboardEvent) {
    if (!this.viewModel) {
      return;
    }

    event.preventDefault();

    const isMac = navigator.platform.toLowerCase().includes('mac');
    const modifier = isMac ? event.metaKey : event.ctrlKey;
    if (!modifier) {
      return;
    }

    if (isMac) {
      // cmd+z and cmd+shift+z on Mac
      if (event.key !== 'z') {
        return;
      }

      if (event.shiftKey) {
        this.viewModel.redo();
      } else {
        this.viewModel.undo();
      }
    } else {
      // ctrl+z and ctrl+y on Windows/Linux
      if (event.shiftKey) {
        return;
      }

      switch (event.key) {
        case 'z':
          this.viewModel.undo();
          return;
        case 'y':
          this.viewModel.redo();
          return;
      }
    }
  }

  private onUndo() {
    this.viewModel.undo();
  }

  private onRedo() {
    this.viewModel.redo();
  }

  private onHint() {
    this.hint();
  }

  private onSolution() {
    this.viewModel.solve();
  }

  private onSave() {
    this.saveCheckpoint();
    this._toast = { role: 'success', message: 'Checkpoint saved' };
  }

  private onRestore() {
    this.restoreCheckpoint();
    this._toast = { role: 'info', message: 'Checkpoint restored' };
  }

  private onRestart() {
    this.checkpointSaved = false;
    this.atCheckpoint = false;
    this.lastSavedState = null;
    this.viewModel.restart();
  }

  private onNewGame() {
    // BR-142: preserve the old save first (gated internally — BR-127),
    // then let the fresh game earn its own saves.
    this.saveOnLeave();
    this.lastSavedState = null;
    // BR-145
    this.checkpointSaved = false;
    this.atCheckpoint = false;
    // BR-141/BR-144: fresh game with the same settings; starting swallows
    // the internal precache completion (same pattern as setPreset).
    this.starting = true;
    this.viewModel.newGame();
    this.starting = false;
    this.emitStatus(this.preset);
  }

  private onChangeOptions() {
    navigate(`/puzzles/${this.puzzleName}/boards`);
  }

  private onPlayAgain() {
    this.checkpointSaved = false;
    this.atCheckpoint = false;
    this.lastSavedState = null;
    this.viewModel.restart();
    this.gameplayState = 'playing';
    this.savedToPresets = false;
    this.emitStatus(this.preset);
  }

  private onBackToPuzzles() {
    navigate(`/puzzles/${this.puzzleName}/boards`);
  }

  private onHowToPlay() {
    window.dispatchEvent(
      new CustomEvent('luna-howto', { detail: { puzzle: this.puzzleName } })
    );
  }

  content() {
    return html`
      <main class="app-puzzle app-puzzle--${this.gameplayState}">
        ${this.renderBody()} ${this.renderConfirm()} ${this.renderToast()}
      </main>
    `;
  }

  private renderConfirm() {
    const restart = this._confirmAction === 'restart';
    return html`
      <luna-confirm-dialog
        .open=${this._confirmAction !== null}
        title=${restart ? 'Restart this puzzle?' : 'Start a new game?'}
        body=${restart
          ? "Your moves so far will be cleared and the board will go back to the start. This can't be undone."
          : "Your progress on this board will be cleared. This can't be undone."}
        confirm-label=${restart ? 'Restart' : 'New game'}
        @luna-confirm=${this.onConfirmAction}
        @luna-cancel=${this.onConfirmCancel}
      ></luna-confirm-dialog>
    `;
  }

  private onConfirmAction() {
    const action = this._confirmAction;
    this._confirmAction = null;
    if (action === 'restart') {
      this.onRestart();
    } else if (action === 'new-game') {
      this.onNewGame();
    }
  }

  private renderToast() {
    return html`
      <luna-toast
        .open=${this._toast !== null}
        role=${this._toast?.role ?? 'success'}
        message=${this._toast?.message ?? ''}
        @luna-dismiss=${this.onToastDismiss}
      ></luna-toast>
    `;
  }

  private onConfirmCancel() {
    this._confirmAction = null;
  }

  private onToastDismiss() {
    this._toast = null;
  }

  private renderBody() {
    // One stable template shape: the canvas node must NEVER be recreated
    // (the wasm draws into it during initialise; Lit would swap in a blank
    // canvas if the branch shape changed). States are overlays on top.
    const covered = this.gameplayState !== 'playing' && this.gameplayState !== 'solved';
    return html`
      <div class="app-puzzle__stage">
        <div class="app-puzzle__stage-content" ?inert=${covered} aria-hidden=${covered ? 'true' : 'false'}>
          ${this.renderGame()}
        </div>
        ${this.gameplayState === 'loading'
          ? html`
              <div class="app-puzzle__skeleton" role="status" aria-label="Loading puzzle">
                <luna-skeleton class="app-puzzle__skeleton-hero"></luna-skeleton>
                <luna-skeleton class="app-puzzle__skeleton-sidebar"></luna-skeleton>
              </div>
            `
          : ''}
        ${this.gameplayState === 'error'
          ? html`
              <div class="app-puzzle__overlay app-puzzle__message" role="alert">
                <p class="mbw-type-body-large">Something went wrong loading this puzzle.</p>
                <luna-button variant="primary" @click=${this.retryLoad}>Try again</luna-button>
              </div>
            `
          : ''}
        ${this.gameplayState === 'empty'
          ? html`
              <div class="app-puzzle__overlay app-puzzle__message">
                <p class="mbw-type-body-large">There is nothing to play here.</p>
              </div>
            `
          : ''}
      </div>
    `;
  }

  private renderGame() {
    const solved = this.gameplayState === 'solved';

    return html`
      <div class="puzzle-main-row">
        <div class="puzzle-main-game puzzle-main-game--${this.puzzleName} ${solved ? 'puzzle-main-game--solved' : ''}">
          <div class="puzzle-resize-wrapper">
            <canvas id="puzzle-canvas" tabindex="0"></canvas>
          </div>
        </div>

        <div class="puzzle-main-controls">
          ${solved ? this.renderSolvedSidebar() : this.renderPlayingSidebar()}
        </div>
      </div>
    `;
  }

  private renderPlayingSidebar() {
    const hintVisible = this.puzzleModel?.hintsSupported ?? false;
    const solutionVisible = (this.puzzleModel?.solutionSupported ?? false) && !this.isParticipant;
    const stuckVisible = hintVisible || solutionVisible;

    return html`
      <div class="app-puzzle__sidebar-content">
        <span class="mbw-type-title-section app-puzzle__name">${this.puzzleModel?.name ?? ''}</span>
        <span class="mbw-type-eyebrow app-puzzle__group">While you play</span>
        <div class="app-puzzle__controls-row">
          ${this.undoEnabled
            ? html`<luna-button variant="primary" size="xl" icon="undo" full @click=${this.onUndo}>Undo</luna-button>`
            : html`<luna-button variant="hatched" label2="Nothing to undo yet" full>Undo</luna-button>`}
          ${this.redoEnabled
            ? html`<luna-button variant="checkpoint" icon="redo" full @click=${this.onRedo}>Redo</luna-button>`
            : html`<luna-button variant="hatched" label2="Nothing to redo yet" full>Redo</luna-button>`}
        </div>

        ${stuckVisible
          ? html`
              <hr class="app-puzzle__divider" />
              <span class="mbw-type-eyebrow app-puzzle__group">Stuck?</span>
              <div class="app-puzzle__controls-row">
                ${hintVisible
                  ? html`<luna-button variant="neutral" icon="hint" full @click=${this.onHint}>Show hint</luna-button>`
                  : ''}
                ${solutionVisible
                  ? html`<luna-button variant="neutral" icon="eye" full @click=${this.onSolution}>Solution</luna-button>`
                  : ''}
              </div>
            `
          : ''}

        <hr class="app-puzzle__divider" />
        <span class="mbw-type-eyebrow app-puzzle__group">Checkpoints</span>
        <div class="app-puzzle__controls-row">
          <luna-button variant="checkpoint" icon="flag" full @click=${this.onSave}>Save</luna-button>
          ${this.checkpointSaved
            ? this.atCheckpoint
              ? html`<luna-button variant="hatched" label2="Already at checkpoint" full>Restore</luna-button>`
              : html`<luna-button variant="checkpoint" icon="restore" full @click=${this.onRestore}>Restore</luna-button>`
            : html`<luna-button variant="hatched" label2="No checkpoint saved" full>Restore</luna-button>`}
        </div>

        <hr class="app-puzzle__divider" />
        <span class="mbw-type-eyebrow app-puzzle__group">When you're done</span>
        <div class="app-puzzle__controls-row">
          <luna-button variant="neutral" icon="restart" full @click=${() => (this._confirmAction = 'restart')}>Restart</luna-button>
          <luna-button variant="success" icon="sparkle" full @click=${() => (this._confirmAction = 'new-game')}>New game</luna-button>
        </div>
        <div class="app-puzzle__controls-row">
          <luna-button variant="neutral" icon="sliders" full @click=${this.onChangeOptions}>Change options</luna-button>
        </div>

        ${!this.preview
          ? html`
              <button type="button" class="app-puzzle__howto" @click=${this.onHowToPlay}>
                <luna-icon name="help" size="18" aria-hidden="true"></luna-icon>
                <span>How to play</span>
              </button>
            `
          : ''}
      </div>
    `;
  }

  private renderSolvedSidebar() {
    return html`
      <div class="app-puzzle__sidebar-content">
        <span class="mbw-type-eyebrow app-puzzle__solved-eyebrow">Solved!</span>
        <span class="mbw-type-title-section app-puzzle__solved-title">Nicely done.</span>
        ${this.savedToPresets
          ? html`<span class="app-puzzle__saved-line">Saved to your presets</span>`
          : ''}
        <div class="app-puzzle__actions">
          <luna-button variant="success" icon="sparkle" full @click=${this.onPlayAgain}>Play again</luna-button>
          <luna-button variant="neutral" full @click=${this.onBackToPuzzles}>Back to puzzles</luna-button>
        </div>
      </div>
    `;
  }

  styles() {
    return css`
      #puzzle-canvas {
        width: 100%;
        touch-action: none;
        display: block;
      }

      .puzzle-main-row {
        display: flex;
        flex-direction: row;
        gap: 22px; /* pinned: figma Gameplay stage gap */
        align-items: stretch;
        justify-content: center;
        box-sizing: border-box;
        padding: 24px; /* pinned: figma Gameplay stage pad */
      }

      .puzzle-main-game {
        flex: 0 1 auto;
        min-width: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        padding: 24px; /* pinned: figma Gameplay hero pad */
        border-radius: var(--luna-radius-board);
        background: var(--mbw-palette-rectangles-tint); /* pinned: figma Gameplay hero (default) */
      }

      /* Per-puzzle hero tint follows the MBW palette token (BR-90). */
      .puzzle-main-game--map {
        background: var(--mbw-palette-map-tint);
      }

      .puzzle-main-game--pegs {
        background: var(--mbw-palette-pegs-tint);
      }

      .puzzle-main-game--rectangles {
        background: var(--mbw-palette-rectangles-tint);
      }

      .puzzle-main-game--tents {
        background: var(--mbw-palette-tents-tint);
      }

      .puzzle-main-game--unruly {
        background: var(--mbw-palette-unruly-tint);
      }

      .puzzle-main-game--untangle {
        background: var(--mbw-palette-untangle-tint);
      }

      .puzzle-main-game--solved {
        background: #e5efe4; /* pinned: figma Gameplay solved hero */
      }

      .puzzle-main-controls {
        flex: 0 0 356px;
        max-width: 100%;
        display: flex;
        flex-direction: column;
        box-sizing: border-box;
        padding: 18px; /* pinned: figma Gameplay sidebar pad */
        border-radius: var(--luna-radius-card);
        background: var(--mbw-surface-card);
        box-shadow: inset 0 0 0 1px var(--mbw-card-hairline), 0 3px 0 var(--mbw-card-ledge);
      }

      .puzzle-resize-wrapper {
        max-width: 100%;
        height: auto;
      }

      @media (min-width: 1280px) {
        .puzzle-resize-wrapper {
          width: min(calc(100dvh - 40px), calc(100vw));
        }
      }

      @media (max-width: 1279px) {
        .puzzle-main-row {
          flex-direction: column;
          justify-content: flex-start;
          align-items: stretch;
        }

        .puzzle-main-game {
          width: 100%;
        }

        .puzzle-main-controls {
          flex: none;
          width: 100%;
        }
      }

      @media (max-width: 767px) {
        .puzzle-main-row {
          padding: 16px; /* pinned: figma Gameplay mobile pad */
        }

        .puzzle-main-game {
          padding: 16px;
        }
      }

      .app-puzzle__sidebar-content {
        display: flex;
        flex-direction: column;
        gap: 12px;
        width: 100%;
      }

      .app-puzzle__group,
      .app-puzzle__solved-eyebrow {
        color: var(--mbw-text-muted);
      }

      /* BR-104: game name lives in the panel, not the top bar (designer directive). */
      .app-puzzle__name {
        color: var(--mbw-text-ink);
      }

      .app-puzzle__controls-row {
        display: flex;
        gap: 10px;
        width: 100%;
      }

      .app-puzzle__controls-row luna-button {
        flex: 1 1 0;
        min-width: 0;
      }

      .app-puzzle__actions {
        display: flex;
        flex-direction: column;
        gap: 10px;
        width: 100%;
      }

      .app-puzzle__actions luna-button {
        display: block;
        width: 100%;
      }

      .app-puzzle__divider {
        border: none;
        border-top: 1px solid var(--luna-surface-divider);
        margin: 4px 0;
      }

      .app-puzzle__howto {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        align-self: center;
        min-height: 44px;
        padding: 8px;
        border: none;
        background: none;
        cursor: pointer;
        color: #0f62fe; /* pinned: figma How to play link */
        font-family: var(--mbw-font-sans);
        font-weight: 700;
        font-size: 17px;
        line-height: 19px;
      }

      .app-puzzle__howto:focus-visible {
        outline: none;
        border-radius: 8px;
        box-shadow: 0 0 0 5px #ffffff, 0 0 0 7px var(--cds-focus);
      }

      .app-puzzle__solved-title {
        margin: 0;
        color: var(--mbw-text-ink);
      }

      .app-puzzle__saved-line {
        color: var(--mbw-text-faint);
        font-family: var(--mbw-font-sans);
        font-weight: 400;
        font-size: 13px; /* pinned: figma auto-save confirmation line */
        line-height: 17px;
      }

      .app-puzzle__stage {
        position: relative;
      }

      .app-puzzle__skeleton {
        position: absolute;
        inset: 0;
        z-index: 1;
        display: flex;
        gap: 22px;
        box-sizing: border-box;
        padding: 24px;
        background: var(--mbw-page);
      }

      .app-puzzle__skeleton-hero {
        flex: 1 1 auto;
        min-width: 0;
        border-radius: var(--luna-radius-board);
      }

      .app-puzzle__skeleton-sidebar {
        flex: 0 0 356px;
        max-width: 100%;
      }

      .app-puzzle__overlay {
        position: absolute;
        inset: 0;
        z-index: 1;
        background: var(--mbw-page);
      }

      .app-puzzle__message {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 16px;
        box-sizing: border-box;
        padding: 48px 24px;
        text-align: center;
      }

      .app-puzzle__message p {
        margin: 0;
        color: var(--mbw-text-body);
      }

      @media (max-width: 1279px) {
        .app-puzzle__skeleton {
          flex-direction: column;
        }

        .app-puzzle__skeleton-sidebar {
          flex: none;
          width: 100%;
        }
      }
    `;
  }
}
